import type { ForqSupabaseClient } from './client';
import {
  normalizeMasterdataItemInput,
  normalizeMasterdataItemSearchInput,
  normalizeSupplierInput,
  normalizeSupplierItemInput,
  normalizeSupplierSearchInput,
  type LinkSupplierItemInput,
  type MasterdataGateway,
  type MasterdataItemPage,
  type MasterdataItemRecord,
  type MasterdataItemSearchInput,
  type SaveMasterdataItemInput,
  type SaveSupplierInput,
  type SupplierPage,
  type SupplierRecord,
  type SupplierSearchInput,
} from '@forq/masterdata-data';
import { SupabaseOperationError } from './errors';

interface ItemSearchRow {
  readonly id: string;
  readonly tenant_id: string;
  readonly item_number: string;
  readonly item_kind: 'article' | 'service';
  readonly name: string;
  readonly description: string;
  readonly unit: MasterdataItemRecord['unit'];
  readonly sales_price: number | string;
  readonly purchase_price: number | string;
  readonly vat_rate: number | string;
  readonly manufacturer: string;
  readonly manufacturer_part_number: string;
  readonly barcode: string;
  readonly stock_tracked: boolean;
  readonly minimum_stock: number | string;
  readonly active: boolean;
  readonly preferred_supplier_id: string | null;
  readonly preferred_supplier_name: string;
  readonly supplier_count: number | string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly active_since: string;
  readonly active_until: string | null;
  readonly total_count: number | string;
}

interface SupplierSearchRow {
  readonly payload: SupplierRecord;
  readonly total_count: number | string;
}

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function item(value: unknown): MasterdataItemRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError('masterdata.item', 'The item payload has an invalid shape.');
  }
  const row = value as MasterdataItemRecord;
  return {
    ...row,
    salesPrice: Number(row.salesPrice ?? 0),
    purchasePrice: Number(row.purchasePrice ?? 0),
    vatRate: Number(row.vatRate ?? 0),
    minimumStock: Number(row.minimumStock ?? 0),
    supplierCount: Number(row.supplierCount ?? 0),
    // Defensiv: vor der Bestand-Migration (20260721100000) liefert die get-RPC diese Felder nicht.
    activeSince: row.activeSince ?? '',
    activeUntil: row.activeUntil ?? null,
  };
}

function supplier(value: unknown): SupplierRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError('masterdata.supplier', 'The supplier payload has an invalid shape.');
  }
  const row = value as SupplierRecord;
  return {
    ...row,
    itemCount: Number(row.itemCount ?? 0),
    items: (row.items ?? []).map((link) => ({
      ...link,
      purchasePrice: Number(link.purchasePrice ?? 0),
      leadTimeDays: link.leadTimeDays === null ? null : Number(link.leadTimeDays),
    })),
    // Defensiv: vor der Bestand-Migration (20260721100000) liefert die get-RPC diese Felder nicht.
    activeSince: row.activeSince ?? '',
    activeUntil: row.activeUntil ?? null,
  };
}

function mapSearchItem(row: ItemSearchRow): MasterdataItemRecord {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    number: row.item_number,
    kind: row.item_kind,
    name: row.name,
    description: row.description ?? '',
    unit: row.unit,
    salesPrice: Number(row.sales_price ?? 0),
    purchasePrice: Number(row.purchase_price ?? 0),
    vatRate: Number(row.vat_rate ?? 0),
    manufacturer: row.manufacturer ?? '',
    manufacturerPartNumber: row.manufacturer_part_number ?? '',
    barcode: row.barcode ?? '',
    stockTracked: Boolean(row.stock_tracked),
    minimumStock: Number(row.minimum_stock ?? 0),
    active: Boolean(row.active),
    preferredSupplierId: row.preferred_supplier_id,
    preferredSupplierName: row.preferred_supplier_name ?? '',
    supplierCount: Number(row.supplier_count ?? 0),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    activeSince: row.active_since ?? '',
    activeUntil: row.active_until ?? null,
  };
}

export class SupabaseMasterdataGateway implements MasterdataGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async searchItems(input: MasterdataItemSearchInput): Promise<MasterdataItemPage> {
    const normalized = normalizeMasterdataItemSearchInput(input);
    const { data, error } = await this.client.rpc('masterdata_item_search', {
      p_tenant_id: normalized.tenantId,
      p_kind: normalized.kind,
      p_query: normalized.query,
      p_active: normalized.status === 'all' ? null : normalized.status === 'active',
      p_bestand_from: normalized.bestandFrom || null,
      p_bestand_to: normalized.bestandTo || null,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('masterdata.items.search', messageOf(error));
    const rows = (data ?? []) as ItemSearchRow[];
    return {
      items: rows.map(mapSearchItem),
      total: Number(rows[0]?.total_count ?? 0),
      page: normalized.page,
      pageSize: normalized.pageSize,
    };
  }

  async getItem(tenantId: string, itemId: string): Promise<MasterdataItemRecord> {
    const { data, error } = await this.client.rpc('masterdata_item_get', {
      p_tenant_id: tenantId,
      p_item_id: itemId,
    });
    if (error) throw new SupabaseOperationError('masterdata.item.get', messageOf(error));
    return item(data);
  }

  async saveItem(input: SaveMasterdataItemInput): Promise<MasterdataItemRecord> {
    const normalized = normalizeMasterdataItemInput(input);
    const { tenantId, itemId, ...payload } = normalized;
    const { data, error } = await this.client.rpc('masterdata_item_save', {
      p_tenant_id: tenantId,
      p_item_id: itemId ?? null,
      p_payload: payload,
    });
    if (error) throw new SupabaseOperationError('masterdata.item.save', messageOf(error));
    return item(data);
  }

  async searchSuppliers(input: SupplierSearchInput): Promise<SupplierPage> {
    const normalized = normalizeSupplierSearchInput(input);
    const { data, error } = await this.client.rpc('masterdata_supplier_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_active: normalized.status === 'all' ? null : normalized.status === 'active',
      p_bestand_from: normalized.bestandFrom || null,
      p_bestand_to: normalized.bestandTo || null,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('masterdata.suppliers.search', messageOf(error));
    const rows = (data ?? []) as SupplierSearchRow[];
    return {
      items: rows.map((row) => supplier(row.payload)),
      total: Number(rows[0]?.total_count ?? 0),
      page: normalized.page,
      pageSize: normalized.pageSize,
    };
  }

  async getSupplier(tenantId: string, supplierId: string): Promise<SupplierRecord> {
    const { data, error } = await this.client.rpc('masterdata_supplier_get', {
      p_tenant_id: tenantId,
      p_supplier_id: supplierId,
    });
    if (error) throw new SupabaseOperationError('masterdata.supplier.get', messageOf(error));
    return supplier(data);
  }

  async saveSupplier(input: SaveSupplierInput): Promise<SupplierRecord> {
    const normalized = normalizeSupplierInput(input);
    const { tenantId, supplierId, ...payload } = normalized;
    const { data, error } = await this.client.rpc('masterdata_supplier_save', {
      p_tenant_id: tenantId,
      p_supplier_id: supplierId ?? null,
      p_payload: payload,
    });
    if (error) throw new SupabaseOperationError('masterdata.supplier.save', messageOf(error));
    return supplier(data);
  }

  async searchArticleOptions(tenantId: string, query = ''): Promise<readonly MasterdataItemRecord[]> {
    const { data, error } = await this.client.rpc('masterdata_article_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('masterdata.articles.options', messageOf(error));
    if (!Array.isArray(data)) throw new SupabaseOperationError('masterdata.articles.options', 'The article options have an invalid shape.');
    return data.map(item);
  }

  async linkSupplierItem(input: LinkSupplierItemInput): Promise<SupplierRecord> {
    const normalized = normalizeSupplierItemInput(input);
    const { tenantId, supplierId, itemId, ...payload } = normalized;
    const { data, error } = await this.client.rpc('masterdata_supplier_item_upsert', {
      p_tenant_id: tenantId,
      p_supplier_id: supplierId,
      p_item_id: itemId,
      p_payload: payload,
    });
    if (error) throw new SupabaseOperationError('masterdata.supplier.item.upsert', messageOf(error));
    return supplier(data);
  }

  async unlinkSupplierItem(tenantId: string, supplierId: string, itemId: string): Promise<SupplierRecord> {
    const { data, error } = await this.client.rpc('masterdata_supplier_item_delete', {
      p_tenant_id: tenantId,
      p_supplier_id: supplierId,
      p_item_id: itemId,
    });
    if (error) throw new SupabaseOperationError('masterdata.supplier.item.delete', messageOf(error));
    return supplier(data);
  }
}
